import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Loader2, RefreshCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const KIND_LABEL = { welcome: "Welcome", group_full: "Group full", group_full_admin: "Group full (admin)", magic_link: "Sign-in link", reminder: "Meeting reminder", contact: "Contact us", broadcast: "Admin email", broadcast_test: "Admin email (test)", member_number: "Member number" };

export const EmailLog = () => {
  const qc = useQueryClient();
  const { data: log = [], isFetching } = useQuery({ queryKey: ["admin-emails"], queryFn: () => api.get("/admin/emails").then((r) => r.data), refetchInterval: 15000 });
  const [busy, setBusy] = useState(false);

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/emails/test");
      toast.success(`Test welcome email queued to ${data.to}`);
      setTimeout(() => qc.invalidateQueries({ queryKey: ["admin-emails"] }), 4000);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-[#1B2A41] border border-[#2C3E55] overflow-hidden">
      <div className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center justify-between border-b border-[#2C3E55]">
        <p className="text-sm text-[#F8FAFC]/70">Every welcome and group‑full email sent by the site. New F.E.R.Ms get the welcome email automatically; all ten members plus the notify address get an email when a group fills.</p>
        <div className="flex gap-2 flex-shrink-0">
          <button onClick={() => qc.invalidateQueries({ queryKey: ["admin-emails"] })} data-testid="admin-emails-refresh" className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-[#2C3E55] hover:border-[#8FA58A]"><RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} /></button>
          <button onClick={sendTest} disabled={busy} data-testid="admin-send-test-email" className="h-10 inline-flex items-center gap-2 px-4 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 font-type font-bold uppercase tracking-wider text-xs">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send me a test
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="admin-emails-table" className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-[#8FA58A] bg-[#121E2B]">
            <tr>{["When", "Type", "To", "Electorate", "Subject", "Status"].map((h) => <th key={h} className="px-4 py-3 font-type">{h}</th>)}</tr>
          </thead>
          <tbody>
            {log.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#F8FAFC]/50" data-testid="admin-emails-empty">No emails sent yet</td></tr>
            ) : log.map((e) => (
              <tr key={e.id} data-testid={`admin-email-row-${e.id}`} className="border-t border-[#2C3E55]/60">
                <td className="px-4 py-2 whitespace-nowrap text-[#F8FAFC]/60">{new Date(e.created_at).toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" })}</td>
                <td className="px-4 py-2 whitespace-nowrap">{KIND_LABEL[e.kind] || e.kind}</td>
                <td className="px-4 py-2 text-[#F8FAFC]/75">{e.to}</td>
                <td className="px-4 py-2">{e.electorate}{e.group_number ? ` · G${e.group_number}` : ""}</td>
                <td className="px-4 py-2 max-w-xs truncate text-[#F8FAFC]/75" title={e.subject}>{e.subject}</td>
                <td className="px-4 py-2">
                  <span title={e.error} className={`text-[11px] px-2 py-0.5 rounded-full ${e.status === "sent" ? "bg-[#8FA58A]/20 text-[#8FA58A]" : "bg-[#E2705A]/20 text-[#E2705A]"}`}>{e.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
